import type { FreightDashboardMode } from "@/constants/freightDashboardTabs.constants"

export type RagStatus = "onTime" | "atRisk" | "delayed" | "unknown"

const RAG_STATUSES: readonly RagStatus[] = [
    "onTime",
    "atRisk",
    "delayed",
    "unknown",
]

export function isRagStatus(value: string): value is RagStatus {
    return (RAG_STATUSES as string[]).includes(value)
}

export const STATUS_COLOR_VARS = {
    good: "rgb(var(--dot-green))",
    warning: "rgb(var(--dot-amber))",
    bad: "rgb(var(--dot-red))",
    neutral: "rgb(var(--dot-gray))",
} as const

export const RAG_STATUS_LABEL_KEYS: Record<RagStatus, string> = {
    onTime: "ragStatus.onTime",
    atRisk: "ragStatus.atRisk",
    delayed: "ragStatus.delayed",
    unknown: "ragStatus.unknown",
}

export const RAG_FILTER_OPTIONS: { id: RagStatus | "all"; labelKey: string }[] =
    [
        { id: "all", labelKey: "modes.all" },
        { id: "delayed", labelKey: RAG_STATUS_LABEL_KEYS.delayed },
        { id: "atRisk", labelKey: RAG_STATUS_LABEL_KEYS.atRisk },
        { id: "unknown", labelKey: RAG_STATUS_LABEL_KEYS.unknown },
        { id: "onTime", labelKey: RAG_STATUS_LABEL_KEYS.onTime },
    ]

export const RAG_PRIORITY: Record<RagStatus, number> = {
    delayed: 0,
    atRisk: 1,
    unknown: 2,
    onTime: 3,
}

export const DOT_CLASS: Record<RagStatus, string> = {
    delayed: "bg-dot-red",
    atRisk: "bg-dot-amber",
    unknown: "bg-dot-gray",
    onTime: "bg-dot-green",
}

export const TEXT_CLASS: Record<RagStatus, string> = {
    delayed: "text-tone-red-ink",
    atRisk: "text-ink",
    unknown: "text-ink-3",
    onTime: "text-ink-2",
}

const ON_TIME_STATUS_CLASSES = {
    bg: {
        critical: "bg-dot-red",
        warn: "bg-dot-amber",
        ok: "bg-dot-green",
    },
    text: {
        critical: "text-tone-red-ink",
        warn: "text-ink",
        ok: "text-ink",
    },
} as const

export function onTimeStatusClass(
    onTimePct: number,
    variant: "bg" | "text"
): string {
    if (onTimePct < 70) return ON_TIME_STATUS_CLASSES[variant].critical
    if (onTimePct < 90) return ON_TIME_STATUS_CLASSES[variant].warn
    return ON_TIME_STATUS_CLASSES[variant].ok
}

export function sortByRagPriority<
    T extends { rag: RagStatus; daysVsEta: number | null },
>(items: T[]): T[] {
    return items.slice().sort((a, b) => {
        const priorityDiff = RAG_PRIORITY[a.rag] - RAG_PRIORITY[b.rag]
        if (priorityDiff !== 0) return priorityDiff
        if (a.daysVsEta === null) return 1
        if (b.daysVsEta === null) return -1
        return b.daysVsEta - a.daysVsEta
    })
}

export {
    FREIGHT_DASHBOARD_MODE_TABS,
    FREIGHT_DASHBOARD_TABS,
    CARRIERS_TAB,
    ACTIVITY_TAB,
    resolveVisibleFreightDashboardTabs,
} from "@/constants/freightDashboardTabs.constants"
export type {
    FreightDashboardMode,
    FreightDashboardTab,
} from "@/constants/freightDashboardTabs.constants"

export function keepPreviousDataForMode(mode: FreightDashboardMode) {
    return <TData extends { serviceType: string }>(
        previous: TData | undefined
    ): TData | undefined =>
        previous?.serviceType === mode ? previous : undefined
}

export type WilsonActivityChannel = "phoneCalls" | "email" | "text" | "teamChat"

export function formatMoney(amount: number, currency: string = "usd"): string {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency.toUpperCase(),
        maximumFractionDigits: 0,
    }).format(amount)
}

export function formatResponseMinutes(minutes: number): string {
    if (minutes < 60) return `${Math.round(minutes)}m`
    const totalHours = Math.floor(minutes / 60)
    const remMinutes = Math.round(minutes % 60)
    if (totalHours < 24) {
        return remMinutes > 0
            ? `${totalHours}h ${remMinutes}m`
            : `${totalHours}h`
    }
    const days = Math.floor(totalHours / 24)
    const remHours = totalHours % 24
    return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`
}

export function formatShortDate(isoOrDateString: string): string {
    const d = new Date(
        isoOrDateString.length <= 10
            ? `${isoOrDateString}T00:00:00Z`
            : isoOrDateString
    )
    if (Number.isNaN(d.getTime())) return isoOrDateString
    return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
    })
}

export function formatWeekLabel(weekStart: string): string {
    const d = new Date(weekStart + "T00:00:00Z")
    if (Number.isNaN(d.getTime())) return weekStart
    return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
    })
}

export function formatReasonLabel(reason: string): string {
    return reason
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, letter => letter.toUpperCase())
}

export function stripMarkdownEmphasis(text: string): string {
    return text.replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*(.+?)\*/g, "$1")
}

export interface DelayReasonExample {
    shipmentId: string
    referenceNumber: string | null
    snippet: string
    sender: string | null
    context: string | null
}

export interface DelayReason {
    tag: string
    label: string
    count: number
    examples: DelayReasonExample[]
}

export function delayReasonBreakdownFor(
    items: {
        id: string
        referenceNumber: string | null
        reasonTags: string[]
        reasonSnippet: string | null
        reasonContext: string | null
        intervenedBy: string | null
        intervenedByIsCartageTeam: boolean
    }[]
): DelayReason[] {
    const counts = new Map<string, number>()
    const examplesByTag = new Map<string, DelayReasonExample[]>()
    for (const item of items) {
        if (item.intervenedByIsCartageTeam) continue
        for (const tag of item.reasonTags) {
            counts.set(tag, (counts.get(tag) ?? 0) + 1)
            if (item.reasonSnippet) {
                const examples = examplesByTag.get(tag) ?? []
                examples.push({
                    shipmentId: item.id,
                    referenceNumber: item.referenceNumber,
                    snippet: item.reasonSnippet,
                    sender: item.intervenedBy,
                    context: item.reasonContext,
                })
                examplesByTag.set(tag, examples)
            }
        }
    }
    return Array.from(counts.entries())
        .map(([tag, count]) => ({
            tag,
            label: formatReasonLabel(tag),
            count,
            examples: examplesByTag.get(tag) ?? [],
        }))
        .sort((a, b) => b.count - a.count)
}

export function offSlaReasonBreakdownFor(
    items: {
        rag: RagStatus
        delayReasons: string[]
    }[],
    translateReason: (tag: string) => string
): DelayReason[] {
    const counts = new Map<string, number>()
    for (const item of items) {
        if (item.rag !== "delayed") continue
        for (const reason of item.delayReasons) {
            counts.set(reason, (counts.get(reason) ?? 0) + 1)
        }
    }
    return Array.from(counts.entries())
        .map(([tag, count]) => ({
            tag,
            label: translateReason(tag),
            count,
            examples: [],
        }))
        .sort((a, b) => b.count - a.count)
}

export interface WhoIntervening {
    category: string
    count: number
}

const SYSTEM_MAILBOX_RE = /^MicrosoftExchange[0-9a-f]{32}@/i

export function whoInterveningBreakdownFor(
    items: {
        interventionCount: number
        intervenedBy: string | null
        intervenedByIsCartageTeam: boolean
    }[]
): WhoIntervening[] {
    const counts = new Map<string, number>()
    for (const item of items) {
        if (item.interventionCount <= 0 || !item.intervenedBy) continue
        if (item.intervenedByIsCartageTeam) continue
        if (SYSTEM_MAILBOX_RE.test(item.intervenedBy)) continue
        counts.set(item.intervenedBy, (counts.get(item.intervenedBy) ?? 0) + 1)
    }
    return Array.from(counts.entries())
        .map(([category, count]) => ({ category, count }))
        .sort((a, b) => b.count - a.count)
}

export type VerdictTier = "good" | "warning" | "bad"

export function computeDashboardVerdict({
    delayedCount,
    openOrders,
    autonomyPct,
}: {
    delayedCount: number
    openOrders: number
    autonomyPct: number
}): VerdictTier {
    const delayedSharePct =
        openOrders > 0 ? (delayedCount / openOrders) * 100 : 0

    if (delayedSharePct > 30 || autonomyPct < 30) return "bad"
    if (delayedSharePct > 15 || autonomyPct < 50) return "warning"
    return "good"
}

export const VERDICT_STATUS_LABEL_KEYS: Record<VerdictTier, string> = {
    good: "confidenceHeader.verdictGood",
    warning: "confidenceHeader.verdictWarning",
    bad: "confidenceHeader.verdictBad",
}

export interface WeekOverWeekDelta {
    direction: "up" | "down" | "flat"
    diff: number
}

export function calendarWeekOverWeekDelta(
    series: (number | null)[]
): WeekOverWeekDelta | null {
    if (series.length < 2) return null
    const current = series[series.length - 1]
    const prior = series[series.length - 2]
    if (current === null || prior === null) return null
    return weekOverWeekDelta([prior, current])
}

export function weekOverWeekDelta(series: number[]): WeekOverWeekDelta | null {
    if (series.length < 2) return null
    const current = series[series.length - 1]
    const prior = series[series.length - 2]
    const diff = current - prior
    if (diff === 0) return { direction: "flat", diff: 0 }
    return { direction: diff > 0 ? "up" : "down", diff }
}

export function formatDeltaLabel(
    delta: WeekOverWeekDelta,
    unit: string
): string {
    const sign = delta.diff > 0 ? "+" : delta.diff < 0 ? "−" : ""
    return `${sign}${Math.abs(delta.diff).toFixed(1)}${unit}`
}
